import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";
import ThemeToggle from "@/components/site/ThemeToggle";

const links = [
  { label: "Portfolio", href: "#projects" },
  { label: "Services", href: "#services" },
  { label: "Team", href: "#team" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
    <header
      id="top"
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-background/85 border-b border-foreground/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-editorial flex items-center justify-between h-16 md:h-20">
        <Logo />

        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleNav(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-[11px] font-body text-foreground/75 hover:text-foreground transition-colors"
              style={{ letterSpacing: "0.28em", textTransform: "uppercase" }}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <a
            href="#contact"
            onClick={(e) => handleNav(e, "#contact")}
            data-testid="nav-cta-start-project"
            className="inline-flex items-center justify-center h-11 px-6 rounded-full font-body text-[11px] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(58,125,123,0.35)]"
            style={{ backgroundColor: "#3a7d7b", color: "#f4f1eb", letterSpacing: "0.24em", textTransform: "uppercase" }}
          >
            Start a Project
          </a>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-hamburger"
            className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-foreground/20"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
    </header>
    {typeof document !== "undefined" && createPortal(
      <div
        data-testid="mobile-menu"
        className={`md:hidden fixed inset-x-0 top-16 bottom-0 border-t border-foreground/10 transition-all duration-300 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        style={{ backgroundColor: "var(--sb-surface, #1a2330)", zIndex: 60 }}
      >
        <div className="container-editorial py-10 flex flex-col gap-6">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleNav(e, l.href)}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className="font-display text-4xl leading-tight tracking-tight text-foreground"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNav(e, "#contact")}
            data-testid="mobile-nav-cta"
            className="mt-4 inline-flex items-center justify-center h-12 px-6 rounded-full self-start font-body text-[11px]"
            style={{ backgroundColor: "#3a7d7b", color: "#f4f1eb", letterSpacing: "0.24em", textTransform: "uppercase" }}
          >
            Start a Project
          </a>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default Nav;
